/**
 * Initials colours (D-189). People reach the app inside many payloads (lists, seats, profiles), each a `PersonLite` or
 * `Self` with a `publicId` and, when chosen, a `tone`. The API client hands every response to `noteTones`, which keeps
 * publicId → tone, so `Avatar` can colour anyone's initials without each screen passing the colour along.
 */
import { AVATAR_TONES } from "@digu/shared";

const tones = new Map<string, number | null>();

/** Walks a response (bounded depth) and remembers every `{ publicId, tone }` it sees, including a tone set to null. */
export function noteTones(data: unknown, depth = 0): void {
  if (!data || typeof data !== "object" || depth > 6) return;
  if (Array.isArray(data)) { for (const x of data) noteTones(x, depth + 1); return; }
  const o = data as Record<string, unknown>;
  if (typeof o.publicId === "string" && "tone" in o) tones.set(o.publicId, typeof o.tone === "number" ? o.tone : null);
  for (const v of Object.values(o)) if (v && typeof v === "object") noteTones(v, depth + 1);
}

export function rememberTone(publicId: string, tone: number | null): void { tones.set(publicId, tone); }

/** The initials disc colour for a person, or null for the default raised disc. */
export function toneColor(publicId: string | null, tone?: number | null): string | null {
  const t = tone !== undefined ? tone : publicId ? tones.get(publicId) : undefined;
  return typeof t === "number" ? AVATAR_TONES[t] ?? null : null;
}
